import React from "react";
import { Badge } from "@/components/ui";
import { Database, GitBranch } from "lucide-react";

const CLASS_TONE = { VERIFIED_FACT: "green", USER_PROVIDED: "navy", ESTIMATE: "yellow", INFERENCE: "neutral", TRANSACTION_HYPOTHESIS: "gold" };
const FRESH_TONE = { fresh: "green", stale: "yellow", expired: "red" };
const SOURCE_TONE = { "Global Database": "gold", Cerved: "navy", Tavily: "neutral", workspace: "neutral", user: "navy" };

const FIELDS = [
  ["name", "Name"], ["vat_id", "VAT ID"], ["company_number", "Company number"], ["country", "Country"],
  ["legal_form", "Legal form"], ["status", "Status"], ["registered_address", "Registered address"],
  ["incorporation_date", "Incorporated"], ["primary_sector", "Sector"], ["industry_code", "ATECO / SIC"],
  ["website", "Website"], ["revenue", "Revenue"], ["ebitda", "EBITDA"], ["net_profit", "Net profit"],
  ["employees", "Employees"], ["credit_score", "Credit score"], ["description", "Description"],
];

export function SourceLabel({ source, testid }) {
  return <Badge tone={SOURCE_TONE[source] || "neutral"} data-testid={testid}><Database size={9} /> Source: {source || "n/a"}</Badge>;
}

const money = (v) => (typeof v === "number" && v ? `€${(v / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 })}M` : v);

// Flatten the tree to rows with depth (avoids recursive components)
function flattenTree(nodes, depth = 0, out = []) {
  (nodes || []).forEach((n) => {
    out.push({ ...n, depth });
    flattenTree(n.children, depth + 1, out);
  });
  return out;
}

function OwnershipTree({ nodes }) {
  const rows = flattenTree(nodes);
  return (
    <ul>
      {rows.map((n, i) => (
        <li key={i} className="py-1 flex items-center gap-2 text-[12.5px]" style={{ paddingLeft: n.depth * 20 }}>
          <GitBranch size={11} className="text-gold" />
          <span className="text-white">{n.name}</span>
          {n.pct != null && <span className="font-mono text-gold">{n.pct}%</span>}
          <span className="text-white/40 text-[11px]">{n.type}</span>
          {n.is_ubo && <Badge tone="gold">UBO</Badge>}
        </li>
      ))}
    </ul>
  );
}

export default function EntityProfile({ entity, testid = "entity-profile" }) {
  if (!entity?.data) return null;
  const d = entity.data;
  const prov = entity.provenance || {};
  const fin = Array.isArray(d.financials) ? d.financials.filter((r) => r && typeof r === "object" && "year" in r) : [];
  return (
    <div className="space-y-5" data-testid={testid}>
      <div className="flex flex-wrap gap-1.5">
        {(entity.sources || []).map((s) => <SourceLabel key={s} source={s} />)}
        {entity.aliases?.length > 0 && <span className="text-[11px] text-white/40">aka {entity.aliases.slice(0, 3).join(" · ")}</span>}
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
        {FIELDS.filter(([k]) => d[k] != null && d[k] !== "").map(([k, label]) => (
          <div key={k} className={k === "description" ? "sm:col-span-2" : ""} data-testid={`${testid}-field-${k}`}>
            <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{label}</div>
            <div className={`text-[13px] text-white/85 ${k === "description" ? "line-clamp-4" : "break-words"}`}>{["revenue", "ebitda", "net_profit"].includes(k) ? money(d[k]) : String(d[k])}</div>
            {prov[k] && (
              <div className="flex flex-wrap gap-1 mt-1">
                <Badge tone={CLASS_TONE[prov[k].classification]}>{prov[k].classification}</Badge>
                <Badge tone={FRESH_TONE[prov[k].freshness]}>{prov[k].freshness}</Badge>
                <span className="text-[10px] text-white/35">{prov[k].source}</span>
              </div>
            )}
          </div>
        ))}
      </div>
      {fin.length > 0 && (
        <div>
          <div className="overline mb-2">Filed financials</div>
          <table className="w-full text-[12px]"><thead><tr className="text-white/40 text-left"><th>Year</th><th>Revenue</th><th>EBITDA</th><th>Net profit</th><th>Assets</th><th>Debt</th></tr></thead>
            <tbody>{fin.map((r, i) => <tr key={i} className="border-t border-white/5 font-mono"><td>{r.year}</td><td>{money(r.revenue)}</td><td>{money(r.ebitda)}</td><td>{money(r.net_profit)}</td><td>{money(r.total_assets)}</td><td>{money(r.total_debt)}</td></tr>)}</tbody>
          </table>
        </div>
      )}
      {Array.isArray(d.ownership) && d.ownership.length > 0 && (
        <div data-testid={`${testid}-ownership`}>
          <div className="overline mb-2">Ownership & UBO</div>
          <OwnershipTree nodes={d.ownership} />
        </div>
      )}
      {entity.field_history && Object.keys(entity.field_history).length > 0 && (
        <details className="text-[12px] text-white/55">
          <summary className="cursor-pointer text-white/45 hover:text-gold">Field history</summary>
          {Object.entries(entity.field_history).map(([f, hist]) => (
            <div key={f} className="mt-1"><span className="text-white/70">{f}:</span> {hist.slice(-3).map((h) => `${String(h.value).slice(0, 40)} (${h.source})`).join(" → ")}</div>
          ))}
        </details>
      )}
      {entity.clara_intelligence && Object.values(entity.clara_intelligence).some((a) => a?.length) && (
        <div className="p-3 rounded-md border border-[#C5A059]/25 bg-[#C5A059]/[0.04]" data-testid={`${testid}-clara-intel`}>
          <div className="overline mb-2 text-gold">Clara intelligence (derived)</div>
          {(entity.clara_intelligence.committee_outputs || []).slice(-3).map((c, i) => (
            <div key={i} className="text-[12px] text-white/75">Committee: <b>{c.recommendation}</b> · score {c.committee_score} · {c.confidence} · {String(c.at).slice(0, 10)}</div>
          ))}
          {(entity.clara_intelligence.due_diligence || []).slice(-3).map((c, i) => (
            <div key={i} className="text-[12px] text-white/75">Due diligence run · {String(c.at).slice(0, 10)}</div>
          ))}
        </div>
      )}
    </div>
  );
}
